import { useMemo } from "react";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Chip } from "../components/Chip";
import { KpiCard } from "../components/KpiCard";
import { SubscriptionTable } from "../components/SubscriptionTable";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import type { Route } from "../routing";
import type { MeDTO } from "@shared/api";

interface Props {
  me: MeDTO;
  onOpenSubscription: (id: string) => void;
  onNav: (r: Route) => void;
}

export function Dashboard({ me, onOpenSubscription, onNav }: Props) {
  const subsQ = useFetch(() => api.subscriptions.list(), []);
  const activityQ = useFetch(() => api.activity.list(6), []);
  const catalogQ = useFetch(() => api.catalog.list(), []);

  const stats = useMemo(() => {
    const subs = subsQ.data ?? [];
    return {
      active: subs.filter((s) => s.status === "active").length,
      renewals: subs.filter((s) => s.status === "renewal-due").length,
      seatsTotal: subs.reduce((a, s) => a + s.seatsTotal, 0),
      seatsUsed: subs.reduce((a, s) => a + s.seatsUsed, 0),
    };
  }, [subsQ.data]);

  const firstName = me.user.name.split(/\s+/)[0] ?? "there";
  const totalSubs = subsQ.data?.length ?? 0;
  const utilization =
    stats.seatsTotal > 0
      ? Math.round((stats.seatsUsed / stats.seatsTotal) * 100)
      : 0;

  return (
    <div className="bbc-screen">
      <div className="bbc-screen-head">
        <div>
          <div className="bbc-eyebrow">
            Partner workspace · {me.partner.name}
          </div>
          <h1 className="bbc-h1">Good morning, {firstName}</h1>
          <p className="bbc-screen-sub">
            {totalSubs > 0
              ? `${totalSubs} subscription${totalSubs === 1 ? "" : "s"} across your portfolio. ${stats.renewals > 0 ? `${stats.renewals} renewal${stats.renewals === 1 ? "" : "s"} need your attention.` : "All renewals are on track."}`
              : "No subscriptions yet — browse the catalog to acquire your first."}
          </p>
        </div>
        <div className="bbc-screen-actions">
          <Button
            variant="secondary"
            onClick={() => onNav("catalog")}
            icon={<span style={{ fontSize: 14 }}>+</span>}
          >
            Acquire subscription
          </Button>
          <Button variant="primary">Generate IP report</Button>
        </div>
      </div>

      <div className="bbc-kpi-grid">
        <KpiCard
          label="Active subscriptions"
          value={stats.active}
          unit={`of ${totalSubs}`}
          delta={12.5}
          hint="quarter-over-quarter"
          sparkline={[3, 3, 4, 4, 4, 5, 5, 6, 6]}
        />
        <KpiCard
          label="Seats deployed"
          value={stats.seatsUsed}
          unit={`/ ${stats.seatsTotal}`}
          delta={8.1}
          hint={`utilization ${utilization}%`}
          sparkline={[68, 72, 80, 78, 86, 92, 98, 104]}
        />
        <KpiCard
          label="Monthly inference calls"
          value="44.3"
          unit="M"
          delta={-2.4}
          hint="vs. last 30 days"
          sparkline={[42, 45, 48, 50, 47, 46, 45, 44, 44]}
        />
        <KpiCard
          label="Renewals due in 90d"
          value={stats.renewals}
          unit="contracts"
          hint={stats.renewals > 0 ? "€118k committed" : "All on track"}
        />
      </div>

      <div className="bbc-dash-grid">
        <Card
          title="Your subscriptions"
          action={
            <div className="bbc-card-actions">
              <button
                type="button"
                className="bbc-text-btn"
                onClick={() => onNav("subscriptions")}
              >
                View all →
              </button>
            </div>
          }
          padded={false}
        >
          {subsQ.loading ? (
            <div className="bbc-screen-loading">Loading subscriptions…</div>
          ) : subsQ.error ? (
            <div className="bbc-error">{subsQ.error.message}</div>
          ) : (
            <SubscriptionTable
              subs={subsQ.data ?? []}
              onOpen={onOpenSubscription}
            />
          )}
        </Card>

        <Card title="Recent activity">
          {activityQ.loading ? (
            <div className="bbc-screen-loading">Loading activity…</div>
          ) : (
            <ul className="bbc-activity">
              {(activityQ.data ?? []).map((a) => (
                <li key={a.id}>
                  <div className="bbc-act-who">
                    <span className="bbc-avatar bbc-avatar-sm">
                      {a.actor
                        .split(/\s+/)
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join("")}
                    </span>
                    <div>
                      <div className="bbc-act-line">
                        <strong>{a.actor}</strong> {a.verb}{" "}
                        <em>{a.object}</em>
                      </div>
                      <div className="bbc-act-meta">
                        {a.note ? `${a.note} · ${a.t}` : a.t}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card
        title="Catalog spotlight"
        action={
          <button
            type="button"
            className="bbc-text-btn"
            onClick={() => onNav("catalog")}
          >
            Browse full catalog →
          </button>
        }
      >
        {catalogQ.loading ? (
          <div className="bbc-screen-loading">Loading catalog…</div>
        ) : (
          <div className="bbc-catalog-spot">
            {(catalogQ.data ?? []).slice(0, 3).map((c) => (
              <article
                key={c.id}
                className="bbc-catalog-card"
                onClick={() => onNav("catalog")}
              >
                <div className="bbc-catalog-head">
                  <span className="bbc-catalog-domain">{c.domain}</span>
                  {c.badge && (
                    <Chip
                      tone={
                        c.badge === "Beta"
                          ? "info"
                          : c.badge === "Preview"
                            ? "muted"
                            : "accent"
                      }
                    >
                      {c.badge}
                    </Chip>
                  )}
                </div>
                <h4>{c.name}</h4>
                <p>{c.summary}</p>
                <div className="bbc-catalog-foot">
                  <span className="mono">
                    From {c.seatsFrom} seats · {c.termFrom} mo
                  </span>
                  <span className="bbc-catalog-arrow">→</span>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
